import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ADMIN_ACCESS_KEY = 'admin_access_token';
const ADMIN_REFRESH_KEY = 'admin_refresh_token';
const ADMIN_USER_KEY = 'admin_user';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export type AdminRole = 'super_admin' | 'photographer' | 'client';

export interface PhotographerProfileInfo {
  display_name: string;
  bio?: string | null;
  avatar_url?: string | null;
  status: 'pending' | 'approved' | 'rejected';
  approved_at?: string | null;
  created_at?: string | null;
}

export interface AdminUserInfo {
  id: string;
  email: string;
  display_name: string;
  role: AdminRole;
  email_verified: boolean;
  photographer_profile?: PhotographerProfileInfo | null;
}

interface AdminAuthContextType {
  isAuthenticated: boolean;
  user: AdminUserInfo | null;
  accessToken: string | null;
  isSuperAdmin: boolean;
  isPhotographer: boolean;
  isApprovedPhotographer: boolean;
  isPendingPhotographer: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

function readStoredUser(): AdminUserInfo | null {
  try {
    const raw = sessionStorage.getItem(ADMIN_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdminUserInfo;
  } catch {
    return null;
  }
}

export function getAdminAccessToken(): string | null {
  return sessionStorage.getItem(ADMIN_ACCESS_KEY);
}

export function adminAuthHeaders(extra?: HeadersInit): HeadersInit {
  const token = getAdminAccessToken();
  const headers: Record<string, string> = {
    ...(extra as Record<string, string> | undefined),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    sessionStorage.getItem(ADMIN_ACCESS_KEY)
  );
  const [user, setUser] = useState<AdminUserInfo | null>(() => readStoredUser());

  const persist = useCallback((access: string, refresh: string, nextUser: AdminUserInfo) => {
    sessionStorage.setItem(ADMIN_ACCESS_KEY, access);
    sessionStorage.setItem(ADMIN_REFRESH_KEY, refresh);
    sessionStorage.setItem(ADMIN_USER_KEY, JSON.stringify(nextUser));
    setAccessToken(access);
    setUser(nextUser);
  }, []);

  const clear = useCallback(() => {
    sessionStorage.removeItem(ADMIN_ACCESS_KEY);
    sessionStorage.removeItem(ADMIN_REFRESH_KEY);
    sessionStorage.removeItem(ADMIN_USER_KEY);
    setAccessToken(null);
    setUser(null);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<{ ok: boolean; error?: string }> => {
      try {
        const res = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), password }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          return { ok: false, error: (json.detail as string) || 'Correo o contraseña incorrectos.' };
        }
        const nextUser = json.user as AdminUserInfo;
        const role = nextUser?.role;
        if (role !== 'super_admin' && role !== 'photographer') {
          return { ok: false, error: 'Esta cuenta no tiene acceso al panel.' };
        }
        persist(json.access_token, json.refresh_token, nextUser);
        return { ok: true };
      } catch {
        return { ok: false, error: 'No se pudo conectar con el servidor.' };
      }
    },
    [persist]
  );

  const logout = useCallback(() => {
    const refresh = sessionStorage.getItem(ADMIN_REFRESH_KEY);
    if (refresh) {
      fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      }).catch(() => undefined);
    }
    clear();
  }, [clear]);

  const refreshUser = useCallback(async () => {
    const token = sessionStorage.getItem(ADMIN_ACCESS_KEY);
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        clear();
        return;
      }
      const json = await res.json();
      const nextUser = (json.user || json) as AdminUserInfo;
      if (nextUser?.role !== 'super_admin' && nextUser?.role !== 'photographer') {
        clear();
        return;
      }
      sessionStorage.setItem(ADMIN_USER_KEY, JSON.stringify(nextUser));
      setUser(nextUser);
    } catch {
      /* ignore */
    }
  }, [clear]);

  useEffect(() => {
    if (accessToken) {
      refreshUser();
    }
  }, [accessToken, refreshUser]);

  const isAuthenticated = Boolean(accessToken && user);
  const isSuperAdmin = user?.role === 'super_admin';
  const isPhotographer = user?.role === 'photographer';
  const photoStatus = user?.photographer_profile?.status;
  const isApprovedPhotographer =
    isSuperAdmin || (isPhotographer && photoStatus === 'approved');
  const isPendingPhotographer = isPhotographer && photoStatus === 'pending';

  return (
    <AdminAuthContext.Provider
      value={{
        isAuthenticated,
        user,
        accessToken,
        isSuperAdmin,
        isPhotographer,
        isApprovedPhotographer,
        isPendingPhotographer,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (ctx === undefined) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return ctx;
};
